import React, { useEffect, useMemo } from 'react';
import './App.css';
import { Analytics } from '@vercel/analytics/react';
import { eIssueStatus, issues, tIssue } from './data/Issues';
import UnplannedIssueCard from './cards/UnplannedIssueCard';
import TeamMemberCard from './cards/TeamMemberCard';
import { team, tTeamMember } from './data/Team';
import Sprint from './cards/Sprint';
import PlannedIssueCard from './cards/PlannedIssueCard';
import EpicProgressCard from './cards/EpicProgress';
import SprintResults from './cards/SprintResults';
import SprintVelocity from './cards/SprintVelocity';
import { customersData, tCustomer, tCustomerFeedback, tCustomerRequirement } from './data/Customers';
import Footer from './components/Footer';
import CustomerFeedbackPopup from './cards/CustomerFeedbackPopup';
import ReleaseCard from './cards/ReleaseCard';
import Header, { UnlockedFeatures } from './components/Header';
import CustomerReportPopup from './cards/CustomerReport';
import FinanceReportPopup from './cards/FinanceReport';
import VelocityReportPopup, { tVelocityRecord } from './cards/VelocityReport';
import TeamMemberReportPopup from './cards/TeamMemberReport';
import DeveloperDetailsPopup from './cards/DeveloperDetailsPopup';
import ReleaseReportPopup, { tReleaseRecord } from './cards/ReleaseReport';
import EpicReportPopup from './cards/EpicReport';
import HireEmployeePopup from './cards/HireEmployeePopup';
import EmployeeQuitPopup from './cards/EmployeeQuitPopup';
import { sortArray, randomizeTeamLevels } from './helper/HelperFunctions';
import StarterScreen from './components/StarterScreen';
import StartGameScreen from './components/StartGameScreen';
import GameOverScreen, { tGameStats } from './components/GameOverScreen';
import BacklogFilter, { tBacklogFilters } from './components/BacklogFilter';
import HighScoresScreen from './components/HighScoresScreen';
import RivalActivityToast from './components/RivalActivityToast';
import { GameState } from './helper/ActivityGenerator';

export type tFinanceRecord = {
  sprint: number;
  income: number;
  wages: number;
  balance: number;
  releaseVersion: number;
};

export interface GameConfig {
  gameName: string;
  teamName: string;
  teamColor: string;
  numberOfSprints: number;
}

function App() {

  const [showGame, setShowGame] = React.useState<boolean>(false);
  const [showStartScreen, setShowStartScreen] = React.useState<boolean>(false);
  const [showHighScores, setShowHighScores] = React.useState<boolean>(false);
  const [gameConfig, setGameConfig] = React.useState<GameConfig | null>(null);
  const [localIssues, setLocalIssues] = React.useState<tIssue[]>([]);
  const [localCustomers, setLocalCustomers] = React.useState<tCustomer[]>(customersData);
  const [sprint, setSprint] = React.useState<number>(0);
  const [sprintProgress, setSprintProgress] = React.useState<number>(0);
  const [localTeam, setLocalTeam] = React.useState(randomizeTeamLevels(team));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mobileGame, setMobileGame] = React.useState<boolean>(false); // is the mobile  game epic unlocked
  const [sprintVelocity, setSprintVelocity] = React.useState<number[]>([]);
  const [velocityHistory, setVelocityHistory] = React.useState<tVelocityRecord[]>([]);
  const [bank, setBank] = React.useState<number>(500);
  const [financeHistory, setFinanceHistory] = React.useState<tFinanceRecord[]>([{
    sprint: 0,
    income: 500,
    wages: 0,
    balance: 500,
    releaseVersion: 0
  }]); 
  const [employeesFired, setEmployeesFired] = React.useState<number>(0);
  const [gameOver, setGameOver] = React.useState<boolean>(false);
  const [maxSprints, setMaxSprints] = React.useState<number>(10);
  const [backlogFilters, setBacklogFilters] = React.useState<tBacklogFilters>({
    epic: '',
    storyPoints: '',
    skill: ''
  });
  const [gameStartTime, setGameStartTime] = React.useState<string | null>(null);

  // Check for URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gameName = params.get('gameName');
    const teamName = params.get('teamName');
    const teamColor = params.get('teamColor');
    const numberOfSprints = params.get('numberOfSprints');

    if (gameName && teamName && teamColor && numberOfSprints) {
      const config: GameConfig = {
        gameName,
        teamName,
        teamColor,
        numberOfSprints: parseInt(numberOfSprints, 10) || 10,
      };
      setGameConfig(config);
      setMaxSprints(config.numberOfSprints);
      setShowStartScreen(true);
    }
  }, []);

  // Achievement unlock state - IDs from Issues.tsx
  const ACHIEVEMENT_IDS = {
    CUSTOMER_FEEDBACK: 121,
    FINANCE: 122,
    EPICS: 123,
    VELOCITY: 124,
    TEAM_MEMBERS: 125,
    RELEASE: 126,
    BACKLOG_FILTER: 127
  };

  const [unlockedFeatures, setUnlockedFeatures] = React.useState<UnlockedFeatures>({
    customerFeedback: false,
    finance: false,
    epics: false,
    velocity: false,
    teamMembers: false,
    release: false,
    backlogFilter: false
  });

  useEffect(() => {
    // Randomize the issues inline and set them to state
    setLocalIssues(
      [...issues].sort(() => Math.random() - 0.5) // Randomize using sort
    );
  }, []);

  // Check for achievement unlocks whenever issues change
  useEffect(() => {
    const checkAchievements = () => {
      const newUnlockedFeatures = { ...unlockedFeatures };
      let hasChanges = false;

      // Check each achievement story - they unlock when status is Closed or Done
      const isStoryCompleted = (storyId: number) => {
        const story = localIssues.find(issue => issue.id === storyId);
        return story && (story.status === eIssueStatus.Closed || story.status === eIssueStatus.Done);
      };

      if (!unlockedFeatures.customerFeedback && isStoryCompleted(ACHIEVEMENT_IDS.CUSTOMER_FEEDBACK)) {
        newUnlockedFeatures.customerFeedback = true;
        hasChanges = true;
      }
      if (!unlockedFeatures.finance && isStoryCompleted(ACHIEVEMENT_IDS.FINANCE)) {
        newUnlockedFeatures.finance = true;
        hasChanges = true;
      }
      if (!unlockedFeatures.epics && isStoryCompleted(ACHIEVEMENT_IDS.EPICS)) {
        newUnlockedFeatures.epics = true;
        hasChanges = true;
      }
      if (!unlockedFeatures.velocity && isStoryCompleted(ACHIEVEMENT_IDS.VELOCITY)) {
        newUnlockedFeatures.velocity = true;
        hasChanges = true;
      }
      if (!unlockedFeatures.teamMembers && isStoryCompleted(ACHIEVEMENT_IDS.TEAM_MEMBERS)) {
        newUnlockedFeatures.teamMembers = true;
        hasChanges = true;
      }
      if (!unlockedFeatures.release && isStoryCompleted(ACHIEVEMENT_IDS.RELEASE)) {
        newUnlockedFeatures.release = true;
        hasChanges = true;
      }
      if (!unlockedFeatures.backlogFilter && isStoryCompleted(ACHIEVEMENT_IDS.BACKLOG_FILTER)) {
        newUnlockedFeatures.backlogFilter = true;
        hasChanges = true;
      }

      if (hasChanges) {
        setUnlockedFeatures(newUnlockedFeatures);
      }
    };

    if (localIssues.length > 0) {
      checkAchievements();
    }
  }, [localIssues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for Mobile Game epic unlock whenever issues change
  useEffect(() => {
    // Only check if Mobile Game is not already unlocked
    if (!mobileGame && localIssues.length > 0) {
      // Count completed epics (all stories released)
      const epicSet = new Set(localIssues.filter(issue => issue.epic !== 'Misc' && issue.epic !== 'Mobile Game').map((issue) => issue.epic));
      const epics = Array.from(epicSet);
      let completedEpicCount = 0;
      
      for (const epicName of epics) {
        const epicIssues = localIssues.filter((issue: tIssue) => issue.epic === epicName);
        const epicReleasedIssues = epicIssues.filter((issue: tIssue) => issue.released);
        
        // An epic is complete when all its stories are released
        if (epicIssues.length > 0 && epicIssues.length === epicReleasedIssues.length) {
          completedEpicCount++;
        }
      }
      
      // Unlock Mobile Game if 3 or more epics are completed
      if (completedEpicCount >= 3) {
        setMobileGame(true);
      }
    }
  }, [localIssues, mobileGame]);

  function handleToggleIssueClick(issueId: number, newStatus: eIssueStatus) {
    if(sprintProgress > 0) return;
    let newIssues = structuredClone(localIssues);
    let issue = newIssues.find((issue: tIssue) => issue.id === issueId); // Explicitly type the 'issue'
    if (issue) {
      issue.status = newStatus;
    }
    setLocalIssues(newIssues);
  }

  function handleStartSprint() {
    setSprint(sprint + 1);
    assignIssuesToTeam(checkAndAdvanceIssueBeforeSprintStart(structuredClone(localIssues)));
    setSprintProgress(1);
  }

  useEffect(() => {
    if (sprintProgress === 0 || sprintProgress === 11) return;

    if (sprintProgress > 1) {
      let newIssues = structuredClone(localIssues);
      let assignedIssues = newIssues.filter((issue: tIssue) => issue.assignee !== 0);
      // first we need to add the story points done to the issues that are in progress
      for (let issue of assignedIssues) {
        let assignee = localTeam.find((member) => member.id === issue.assignee);
        if (assignee) {
          issue.storyPointsDone = issue.storyPointsDone + (assignee.skill === issue.mainElement ? assignee.level * 1.5 : assignee.level * .75);
        }
      }

      // next check if the progress should move in
      for (let issue of assignedIssues) {
        if(issue.status === eIssueStatus.InQA && issue.storyPointsDone >= issue.storyPoints) {
          issue.qa = issue.assignee;
          issue.assignee = 0;
          issue.status = eIssueStatus.Done;
        }
        if(issue.status === eIssueStatus.InProgress && issue.storyPointsDone >= (issue.storyPoints * 0.7)) {
          issue.dev = issue.assignee;
          issue.assignee = 0;
          issue.status = eIssueStatus.InQA;
        }
      }

      assignIssuesToTeam(newIssues);
    }

    // delay for 6 seconds before moving to the next day
    const timer = setTimeout(() => {
      setSprintProgress(sprintProgress + 1);
    }, 6000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprintProgress]);

  function checkAndAdvanceIssueBeforeSprintStart(issues: tIssue[]) {
    let newIssues = structuredClone(issues);
    for (let issue of newIssues) {
      if (issue.status === eIssueStatus.Todo && issue.storyPointsDone >= issue.storyPoints * 0.7) {
        issue.status = eIssueStatus.InQA;
      }
    }
    return newIssues;
  }
 
  
  //#region assign issues to team
  function assignIssuesToTeam(newIssues: tIssue[]) {
    // Loop through each member of the team to check if they have any issues assigned to them

    for (let member of [...localTeam].filter(member => member.currentlyEmployed).reverse()) {
      let issue = newIssues.find((issue: tIssue) => issue.assignee === member.id);
      
      if (!issue) {
        // The member has no issue assigned, so we need to try and assign one
        let availableIssues = newIssues.filter((issue: tIssue) =>
          (issue.status === eIssueStatus.Todo || issue.status === eIssueStatus.InProgress || issue.status === eIssueStatus.InQA) &&
          issue.assignee === 0
        );
  
        if (availableIssues.length > 0) {
          // First, filter available issues to only those that match the team member's skill
          // Also exclude QA issues where this member did the coding
          let skillMatchingIssues = availableIssues.filter((issue: tIssue) => 
            issue.mainElement === member.skill && 
            (issue.status !== eIssueStatus.InQA || issue.dev !== member.id)
          );
  
          if (skillMatchingIssues.length > 0) {
            // If we have issues matching the member's skill, assign the first one
            skillMatchingIssues[0].assignee = member.id;
          } else {
            // If no matching skill issue found, handle special cases for DevOps and others
            if (member.skill === 'DevOps') {
              // DevOps can be assigned issues in QA if no matching issues are found
              // Filter out QA issues where this member did the coding (dev field)
              let qaIssues = availableIssues.filter((issue: tIssue) => 
                issue.status === eIssueStatus.InQA && issue.dev !== member.id
              );
              if (qaIssues.length > 0) {
                qaIssues[0].assignee = member.id;
              }
            } else {
              // If no issues are available for the team member's skill, assign the first available issue
              // But avoid assigning QA issues where this member did the coding
              let nonQAIssues = availableIssues.filter((issue: tIssue) => 
                issue.status !== eIssueStatus.InQA || issue.dev !== member.id
              );
              if (nonQAIssues.length > 0) {
                nonQAIssues[0].assignee = member.id;
              } else {
                // If all available issues are QA issues this member coded, assign the first available
                availableIssues[0].assignee = member.id;
              }
            }
          }
        }
      }
    }
    // loop through the newIssues and set the status to InProgress if the assignee is not 0 and the status is currently Todo
    for (let issue of newIssues) {
      if (issue.assignee !== 0 && issue.status === eIssueStatus.Todo) {
        issue.status = eIssueStatus.InProgress;
      }
    }
    setLocalIssues(newIssues);
  }
  //#endregion
  
  //#region close sprint
  function closeModal(lt: tTeamMember[]) {
    let newIssues = structuredClone(localIssues);
    // how many story points were completed in the sprint?
    let completedStoryPoints = newIssues.filter((issue: tIssue) => issue.status === eIssueStatus.Done).reduce((acc: number, issue: tIssue) => acc + issue.storyPoints, 0);
    // add completed story points to the sprint velocity
    setSprintVelocity([...sprintVelocity, completedStoryPoints]);
    
    // Calculate per-developer velocity
    const developerVelocity: { [developerId: number]: { code: number; qa: number; name: string; icon: string } } = {};
    
    // Track code story points (dev field) and QA story points (qa field)
    // Dev gets credit for 70% of story points (coding phase)
    // QA gets credit for remaining 30% (testing phase)
    // We calculate dev first, then qa gets the remainder to ensure totals match exactly
    newIssues.filter((issue: tIssue) => issue.status === eIssueStatus.Done).forEach((issue: tIssue) => {
      const codePoints = Math.round(issue.storyPoints * 0.7);
      const qaPoints = issue.storyPoints - codePoints; // Remainder ensures exact total
      
      if (issue.dev) {
        const devMember = lt.find(m => m.id === issue.dev);
        if (devMember) {
          if (!developerVelocity[issue.dev]) {
            developerVelocity[issue.dev] = { code: 0, qa: 0, name: devMember.name, icon: devMember.icon };
          }
          developerVelocity[issue.dev].code += codePoints;
        }
      }
      if (issue.qa) {
        const qaMember = lt.find(m => m.id === issue.qa);
        if (qaMember) {
          if (!developerVelocity[issue.qa]) {
            developerVelocity[issue.qa] = { code: 0, qa: 0, name: qaMember.name, icon: qaMember.icon };
          }
          developerVelocity[issue.qa].qa += qaPoints;
        }
      }
    });
    
    // Add to velocity history
    setVelocityHistory([...velocityHistory, {
      sprint: sprint,
      totalVelocity: completedStoryPoints,
      developerVelocity: developerVelocity
    }]);
    
    // any issue that is in Done should be closed
    for (let issue of newIssues) {
      if (issue.status === eIssueStatus.Done) {
        issue.status = eIssueStatus.Closed;
      }
      if (issue.status === eIssueStatus.Todo || issue.status === eIssueStatus.InProgress || issue.status === eIssueStatus.InQA) {
        issue.assignee = 0;
        issue.status = eIssueStatus.Open;
      }
    }
    
    // Calculate income from customers
    let income = 0;
    for (let customer of localCustomers) {
      if (customer.isCustomer && customer.numberOfSites > 0) {
        income += customer.paymentPerSprint * customer.numberOfSites;
      }
    }
    
    // Calculate team wages (outgoings)
    let wages = 0;
    for (let member of lt) {
      wages += member.level * 50;
    }
    
    const newBank = bank + income - wages;
    setBank(newBank);
    
    // Record finance history
    setFinanceHistory([...financeHistory, {
      sprint: sprint,
      income: income,
      wages: wages,
      balance: newBank,
      releaseVersion: releaseVersion
    }]);
    
    setLocalIssues(newIssues);
    
    // Merge the updated team members (with level changes) back into the full team
    // This preserves unemployed developers while updating the levels of employed ones
    const updatedTeam = structuredClone(localTeam);
    lt.forEach((updatedMember: tTeamMember) => {
      const memberInTeam = updatedTeam.find((m: tTeamMember) => m.id === updatedMember.id);
      if (memberInTeam) {
        memberInTeam.level = updatedMember.level;
      }
    });
    setLocalTeam(updatedTeam);
    
    // Check if any highest-level employees should quit
    checkForEmployeeDeparture(updatedTeam);
    
    // Check for game over conditions - pass the new sprint velocity array
    const newSprintVelocity = [...sprintVelocity, completedStoryPoints];
    checkGameOver(newBank, newIssues, newSprintVelocity);
    
    setSprintProgress(0);
  }
  
  function checkGameOver(currentBank: number, currentIssues: tIssue[], currentSprintVelocity: number[]) {
    // Condition 1: Reached max sprints
    if (sprint >= maxSprints) {
      setGameOver(true);
      // Save score asynchronously
      const stats = calculateGameStats(currentIssues, currentBank, currentSprintVelocity);
      saveScore(stats);
      return;
    }
    
    // Condition 2: All epics completed
    const epicSet = new Set(currentIssues.map((issue: tIssue) => issue.epic));
    const epics = Array.from(epicSet);
    let allEpicsComplete = true;
    
    for (const epicName of epics) {
      const epicIssues = currentIssues.filter((issue: tIssue) => issue.epic === epicName);
      const epicReleasedIssues = epicIssues.filter((issue: tIssue) => issue.released);
      
      if (epicIssues.length !== epicReleasedIssues.length) {
        allEpicsComplete = false;
        break;
      }
    }
    
    if (allEpicsComplete) {
      setGameOver(true);
      // Save score asynchronously
      const stats = calculateGameStats(currentIssues, currentBank, currentSprintVelocity);
      saveScore(stats);
      return;
    }
    
    // Condition 3: Bank balance is -100 or less
    if (currentBank <= -100) {
      setGameOver(true);
      // Save score asynchronously
      const stats = calculateGameStats(currentIssues, currentBank, currentSprintVelocity);
      saveScore(stats);
      return;
    }
  }
  
  function checkForEmployeeDeparture(team: tTeamMember[]) {
    // Find currently employed developers
    const employedDevelopers = team.filter((member: tTeamMember) => member.currentlyEmployed);
    
    if (employedDevelopers.length === 0) return;
    
    // Find the highest level among employed developers
    const highestLevel = Math.max(...employedDevelopers.map((member: tTeamMember) => member.level));
    
    // Get all developers at the highest level
    const highestLevelDevelopers = employedDevelopers.filter((member: tTeamMember) => member.level === highestLevel);
    
    // Check each highest-level developer for departure (1 in 4 chance)
    for (const developer of highestLevelDevelopers) {
      const randomRoll = Math.random();
      if (randomRoll < 0.25) { // 1 in 4 chance (25%)
        // This developer is leaving
        setQuittingEmployeeId(developer.id);
        setEmployeeQuitPopupOpen(true);
        // Only process one departure at a time
        return;
      }
    }
  }
  //#endregion

  //#region release stuff

  const [releaseVersion, setReleaseVersion] = React.useState<number>(0);

  function handleRelease() {
    let newLocalIssues = structuredClone(localIssues);
    // any issues that have a status of closed should be set to released
    const storiesInThisRelease = newLocalIssues.filter((issue: tIssue) => issue.status === eIssueStatus.Closed && !issue.released).length;
    
    for (let issue of newLocalIssues) {
      if (issue.status === eIssueStatus.Closed) {
        issue.released = true;
      }
    }
    
    // Check if any epic was completed with this release
    // An epic is completed if all its stories are now released
    const epicSet = new Set(newLocalIssues.map((issue: tIssue) => issue.epic));
    const epics = Array.from(epicSet);
    let epicCompletedInThisRelease = false;
    
    for (const epicName of epics) {
      const epicIssues = newLocalIssues.filter((issue: tIssue) => issue.epic === epicName);
      const epicReleasedIssues = epicIssues.filter((issue: tIssue) => issue.released);
      
      // Check if all stories in this epic are now released
      if (epicIssues.length === epicReleasedIssues.length) {
        // Check if this epic was NOT fully released before this release
        const epicIssuesBeforeRelease = localIssues.filter((issue: tIssue) => issue.epic === epicName);
        const epicReleasedIssuesBeforeRelease = epicIssuesBeforeRelease.filter((issue: tIssue) => issue.released);
        
        if (epicIssuesBeforeRelease.length !== epicReleasedIssuesBeforeRelease.length) {
          epicCompletedInThisRelease = true;
          break;
        }
      }
    }
    
    // Calculate new version based on epic completion
    let newVersion: number;
    if (releaseVersion === 0) {
      newVersion = 1.0;
    } else if (epicCompletedInThisRelease) {
      // Increment major version and reset decimal part
      newVersion = Math.floor(releaseVersion) + 1.0;
    } else {
      // Increment by 0.1
      newVersion = releaseVersion + 0.1;
    }
    setReleaseVersion(newVersion);
    
    // Calculate total released stories
    const totalReleasedStories = newLocalIssues.filter((issue: tIssue) => issue.released).length;
    
    // Add to release history
    setReleaseHistory([...releaseHistory, {
      sprint: sprint,
      version: newVersion.toFixed(1),
      storiesInRelease: storiesInThisRelease,
      totalStories: totalReleasedStories
    }]);
    
    setLocalIssues(newLocalIssues);
    // we now need to loop through the customers, check how many requirements they have that are released.  If they have any we set IsCustomer to true, and set the number of sites to the number of met requirements.
    let newLocalCustomers = structuredClone(localCustomers);
    for (let customer of newLocalCustomers) {
      let releasedRequirements = customer.requirements.filter((requirement: tCustomerRequirement) => newLocalIssues.find((issue: tIssue) => issue.id === requirement.requirement && issue.released));
      if (releasedRequirements.length) {
        customer.isCustomer = true;
        customer.numberOfSites = releasedRequirements.length;
      }
    }
    setLocalCustomers(newLocalCustomers);
  }

  //#endregion

  //#region footer stuff

  const releasedIssues = localIssues.filter(i => i.released); // TODO: this might need to be state
  const [customerFeedbackString, setCustomerFeedbackString] = React.useState<string[]>([]);
  const [customerFeedback, setCustomerFeedback] = React.useState<tCustomerFeedback[]>([]);
  const [customerFeedbackPopupOpen, setCustomerFeedbackPopupOpen] = React.useState<boolean>(false);
  const [customerReportPopupOpen, setCustomerReportPopupOpen] = React.useState<boolean>(false);
  const [financeReportPopupOpen, setFinanceReportPopupOpen] = React.useState<boolean>(false);
  const [velocityReportPopupOpen, setVelocityReportPopupOpen] = React.useState<boolean>(false);
  const [teamMemberReportPopupOpen, setTeamMemberReportPopupOpen] = React.useState<boolean>(false);
  const [developerDetailsPopupOpen, setDeveloperDetailsPopupOpen] = React.useState<boolean>(false);
  const [selectedDeveloperId, setSelectedDeveloperId] = React.useState<number | null>(null);
  const [releaseReportPopupOpen, setReleaseReportPopupOpen] = React.useState<boolean>(false);
  const [releaseHistory, setReleaseHistory] = React.useState<tReleaseRecord[]>([]);
  const [epicReportPopupOpen, setEpicReportPopupOpen] = React.useState<boolean>(false);
  const [hireEmployeePopupOpen, setHireEmployeePopupOpen] = React.useState<boolean>(false);
  const [employeeQuitPopupOpen, setEmployeeQuitPopupOpen] = React.useState<boolean>(false);
  const [quittingEmployeeId, setQuittingEmployeeId] = React.useState<number | null>(null);

  useEffect(() => { 
    let newCustomerFeedback: tCustomerFeedback[] = [];
    let newCustomerFeedbackString: string[] = [];
    localCustomers.filter((c: tCustomer) => c.available <= sprint).forEach(customer => {
      let tempRequirements: string[] = [];
      customer.requirements.forEach(requirement => {
          if (!releasedIssues.find(issue => issue.id === requirement.requirement)) {
              tempRequirements.push(requirement.feedback);
          }
      });
      if (tempRequirements.length){
        let requirementToUse = tempRequirements[Math.floor(Math.random() * tempRequirements.length)];
        newCustomerFeedbackString.push(`${customer.icon} ${customer.name}: ${requirementToUse}`);
        newCustomerFeedback.push({customerId: customer.id, feedback: requirementToUse});
      }
    });
    setCustomerFeedback(newCustomerFeedback);
    setCustomerFeedbackString(newCustomerFeedbackString);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprint, releaseVersion]);

  //#endregion

  //#region developer details
  function handleDeveloperClick(developerId: number) {
    setSelectedDeveloperId(developerId);
    setDeveloperDetailsPopupOpen(true);
  }

  function closeDeveloperDetails() {
    setDeveloperDetailsPopupOpen(false);
    setSelectedDeveloperId(null);
  }

  function handleDismissEmployee(developerId: number) {
    // Set the developer's currentlyEmployed status to false
    const newTeam = structuredClone(localTeam);
    const developer = newTeam.find((member: tTeamMember) => member.id === developerId);
    if (developer) {
      developer.currentlyEmployed = false;
    }
    setLocalTeam(newTeam);
    
    // Increment fired count
    setEmployeesFired(employeesFired + 1);
    
    // Close the developer details popup
    setDeveloperDetailsPopupOpen(false);
    setSelectedDeveloperId(null);
    
    // Open the hire employee popup
    setHireEmployeePopupOpen(true);
  }

  function handleHireEmployee(developerId: number) {
    // Set the developer's currentlyEmployed status to true
    const newTeam = structuredClone(localTeam);
    const developer = newTeam.find((member: tTeamMember) => member.id === developerId);
    if (developer) {
      developer.currentlyEmployed = true;
    }
    setLocalTeam(newTeam);
    
    // Close the hire employee popup
    setHireEmployeePopupOpen(false);
  }

  // Get 4 random unemployed developers for hiring
  function getUnemployedDevelopers(): tTeamMember[] {
    const unemployed = localTeam.filter((member: tTeamMember) => !member.currentlyEmployed);
    // Shuffle and return up to 4
    const shuffled = [...unemployed].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 4);
  }
  
  function handleEmployeeQuit() {
    if (quittingEmployeeId === null) return;
    
    // Set the developer's currentlyEmployed status to false
    const newTeam = structuredClone(localTeam);
    const developer = newTeam.find((member: tTeamMember) => member.id === quittingEmployeeId);
    if (developer) {
      developer.currentlyEmployed = false;
    }
    setLocalTeam(newTeam);
    
    // Close the employee quit popup
    setEmployeeQuitPopupOpen(false);
    setQuittingEmployeeId(null);
    
    // Open the hire employee popup
    setHireEmployeePopupOpen(true);
  }
  //#endregion

  //#region backlog filtering
  function filterBacklogIssues(issues: tIssue[]): tIssue[] {
    return issues.filter((issue) => {
      // Filter out Mobile Game stories if the epic is locked
      if (issue.epic === 'Mobile Game' && !mobileGame) {
        return false;
      }

      // Filter by epic
      if (backlogFilters.epic && issue.epic !== backlogFilters.epic) {
        return false;
      }

      // Filter by story points
      if (backlogFilters.storyPoints) {
        const [min, max] = backlogFilters.storyPoints.split('-').map(Number);
        if (issue.storyPoints < min || issue.storyPoints > max) {
          return false;
        }
      }

      // Filter by skill
      if (backlogFilters.skill && issue.mainElement !== backlogFilters.skill) {
        return false;
      }

      return true;
    });
  }

  function getAvailableEpics(): string[] {
    const epicSet = new Set(
      localIssues
        .filter((issue) => issue.available <= sprint && issue.status === eIssueStatus.Open)
        .map((issue) => issue.epic)
    );
    return Array.from(epicSet).sort();
  }
  //#endregion

  //#region game over
  function calculateGameStats(currentIssues?: tIssue[], currentBank?: number, currentSprintVelocity?: number[]): tGameStats {
    // Use provided values or fall back to state
    const issues = currentIssues || localIssues;
    const bankBalance = currentBank !== undefined ? currentBank : bank;
    const velocity = currentSprintVelocity || sprintVelocity;
    
    // Calculate total epics completed
    const epicSet = new Set(issues.map((issue: tIssue) => issue.epic));
    const epics = Array.from(epicSet);
    let completedEpics = 0;
    
    for (const epicName of epics) {
      const epicIssues = issues.filter((issue: tIssue) => issue.epic === epicName);
      const epicReleasedIssues = epicIssues.filter((issue: tIssue) => issue.released);
      
      if (epicIssues.length === epicReleasedIssues.length && epicIssues.length > 0) {
        completedEpics++;
      }
    }
    
    // Calculate most and fewest story points in a sprint
    const mostStoryPoints = velocity.length > 0 ? Math.max(...velocity) : 0;
    const fewestStoryPoints = velocity.length > 0 ? Math.min(...velocity) : 0;
    
    return {
      companyName: gameConfig?.teamName || 'DemoSoft',
      totalSprints: sprint,
      totalStoriesReleased: issues.filter((issue: tIssue) => issue.released).length,
      totalEpicsCompleted: completedEpics,
      signedUpCustomers: localCustomers.filter((c: tCustomer) => c.isCustomer).length,
      bankBalance: bankBalance,
      employeesFired: employeesFired,
      mostStoryPointsInSprint: mostStoryPoints,
      fewestStoryPointsInSprint: fewestStoryPoints,
      releaseVersion: releaseVersion,
    };
  }

  async function saveScore(stats: tGameStats) {
    try {
      // Calculate score the same way GameOverScreen does
      let score = 0;
      score += stats.bankBalance * 10;
      score = score / stats.totalSprints;
      score += Math.floor(stats.releaseVersion) * 10;
      score += stats.totalStoriesReleased;
      score -= stats.employeesFired * 10;
      score += stats.mostStoryPointsInSprint;
      score += stats.fewestStoryPointsInSprint;
      score += stats.signedUpCustomers * 10;
      const finalScore = Math.round(score);

      // Use the game name from config, or 'demo' for demo games
      const gameName = gameConfig?.gameName || 'demo';

      const response = await fetch('/api/save-score', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyName: stats.companyName,
          score: finalScore,
          totalSprints: stats.totalSprints,
          totalStoriesReleased: stats.totalStoriesReleased,
          totalEpicsCompleted: stats.totalEpicsCompleted,
          signedUpCustomers: stats.signedUpCustomers,
          bankBalance: stats.bankBalance,
          employeesFired: stats.employeesFired,
          mostStoryPointsInSprint: stats.mostStoryPointsInSprint,
          fewestStoryPointsInSprint: stats.fewestStoryPointsInSprint,
          releaseVersion: stats.releaseVersion,
          gameName,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log('Score saved successfully:', data);
      } else {
        console.error('Failed to save score:', data);
      }
    } catch (error) {
      console.error('Error saving score:', error);
    }
  }
  //#endregion

  //#region activity state
  // Calculate game state for activity generation
  const calculateGameStateForActivities = useMemo((): GameState => {
    // Calculate completed epics and epic progress
    const epicSet = new Set(localIssues.filter(issue => issue.epic !== 'Misc').map((issue: tIssue) => issue.epic));
    const epics = Array.from(epicSet);
    let completedEpics = 0;
    const epicProgress: { epicName: string; percentage: number }[] = [];
    
    for (const epicName of epics) {
      const epicIssues = localIssues.filter((issue: tIssue) => issue.epic === epicName);
      const epicReleasedIssues = epicIssues.filter((issue: tIssue) => issue.released);
      const percentage = epicIssues.length > 0 ? Math.round((epicReleasedIssues.length / epicIssues.length) * 100) : 0;
      
      if (epicIssues.length === epicReleasedIssues.length && epicIssues.length > 0) {
        completedEpics++;
      }
      
      epicProgress.push({ epicName, percentage });
    }
    
    // Calculate story points this sprint (from Done issues)
    const storyPointsThisSprint = localIssues
      .filter((issue: tIssue) => issue.status === eIssueStatus.Done)
      .reduce((acc: number, issue: tIssue) => acc + issue.storyPoints, 0);
    
    // Calculate sprint percentage completed (done vs planned)
    const totalPlannedPoints = localIssues
      .filter((issue: tIssue) => 
        issue.status === eIssueStatus.Done || 
        issue.status === eIssueStatus.InProgress || 
        issue.status === eIssueStatus.InQA ||
        issue.status === eIssueStatus.Todo
      )
      .reduce((acc: number, issue: tIssue) => acc + issue.storyPoints, 0);
    const sprintPercentageCompleted = totalPlannedPoints > 0 
      ? Math.round((storyPointsThisSprint / totalPlannedPoints) * 100) 
      : 0;
    
    // Determine epic focus (more than 2 stories from same non-Misc epic this sprint)
    const thisSprintIssues = localIssues.filter((issue: tIssue) => 
      issue.status === eIssueStatus.Done || 
      issue.status === eIssueStatus.InProgress || 
      issue.status === eIssueStatus.InQA
    );
    const epicCounts: { [key: string]: number } = {};
    thisSprintIssues.forEach((issue: tIssue) => {
      if (issue.epic !== 'Misc') {
        epicCounts[issue.epic] = (epicCounts[issue.epic] || 0) + 1;
      }
    });
    const focusedEpic = Object.entries(epicCounts).find(([_, count]) => count > 2);
    
    // Get customer names
    const customerNames = localCustomers
      .filter((c: tCustomer) => c.isCustomer)
      .map((c: tCustomer) => c.name);
    
    return {
      companyName: gameConfig?.teamName || 'DemoSoft',
      sprintsCompleted: sprint,
      storiesReleased: localIssues.filter((issue: tIssue) => issue.released).length,
      epicsCompleted: completedEpics,
      epicProgress,
      customersSignedUp: localCustomers.filter((c: tCustomer) => c.isCustomer).length,
      customerNames,
      bankBalance: bank,
      employeesFired,
      storyPointsThisSprint,
      sprintPercentageCompleted,
      epicFocus: focusedEpic ? focusedEpic[0] : null,
      releaseVersion,
    };
  }, [localIssues, localCustomers, gameConfig, sprint, bank, employeesFired, releaseVersion]);
  //#endregion

  //#region return
  if (showHighScores) {
    return <HighScoresScreen onBack={() => setShowHighScores(false)} />;
  }

  if (gameOver) {
    return <GameOverScreen stats={calculateGameStats()} customers={localCustomers} />;
  }

  if (showStartScreen && gameConfig) {
    return (
      <StartGameScreen
        gameName={gameConfig.gameName}
        teamName={gameConfig.teamName}
        teamColor={gameConfig.teamColor}
        numberOfSprints={gameConfig.numberOfSprints}
        onStartGame={() => {
          setGameStartTime(new Date().toISOString());
          setShowStartScreen(false);
          setShowGame(true);
        }}
      />
    );
  }

  if (!showGame) {
    return (
      <StarterScreen 
        onDemoGame={() => {
          setGameStartTime(new Date().toISOString());
          setShowGame(true);
        }}
        onSetupGame={() => {}}
        onHighScores={() => setShowHighScores(true)}
      />
    );
  }

  return (
  <div className="app">
    <Analytics />
  <Header 
    bank={bank} 
    openFinanceReportCallback={() => setFinanceReportPopupOpen(true)} 
    openCustomerReportCallback={() => setCustomerReportPopupOpen(true)} 
    openVelocityReportCallback={() => setVelocityReportPopupOpen(true)}
    openTeamMemberReportCallback={() => setTeamMemberReportPopupOpen(true)}
    openReleaseReportCallback={() => setReleaseReportPopupOpen(true)}
    openEpicReportCallback={() => setEpicReportPopupOpen(true)}
    openFeedbackCallback={() => setCustomerFeedbackPopupOpen(true)}
    customers={localCustomers} 
    team={localTeam.filter(member => member.currentlyEmployed)}
    sprint={sprint}
    sprintVelocity={sprintVelocity}
    releaseVersion={releaseVersion}
    issues={localIssues}
    customerFeedback={customerFeedbackString}
    unlockedFeatures={unlockedFeatures}
    gameConfig={gameConfig}
  />
  <RivalActivityToast
    gameName={gameConfig?.gameName || 'demo'}
    companyName={gameConfig?.teamName || 'DemoSoft'}
    companyColour={gameConfig?.teamColor || '#808080'}
    gameStartTime={gameStartTime}
    enabled={sprintProgress === 11}
    gameState={calculateGameStateForActivities}
  />
  {sprintProgress === 11 ?
  <SprintResults
      isOpen={sprintProgress === 11}
      completedStoryPoints={localIssues.filter((issue) => issue.status === eIssueStatus.Done).reduce((acc, issue) => acc + issue.storyPoints, 0)}
      totalStoryPoints={localIssues.filter((issue) => issue.status !== eIssueStatus.Closed && issue.status !== eIssueStatus.Open).reduce((acc, issue) => acc + issue.storyPoints, 0)}
      closeModal={(lt) => closeModal(lt)}
      issues={localIssues.filter((issue) => issue.status === eIssueStatus.Done || issue.status === eIssueStatus.InQA || issue.status === eIssueStatus.InProgress)}
      team={localTeam.filter(member => member.currentlyEmployed)}
  /> : null}
  {customerFeedbackPopupOpen ? <CustomerFeedbackPopup customers={sortArray(localCustomers, 'name')} customerFeedback={customerFeedback} closeFeedbackCallback={() => setCustomerFeedbackPopupOpen(false)} /> : null} 
  {customerReportPopupOpen ? <CustomerReportPopup customers={sortArray(localCustomers, 'name')} closeCustomerReportCallback={() => setCustomerReportPopupOpen(false)} sprint={sprint} /> : null}
  {financeReportPopupOpen ? <FinanceReportPopup financeHistory={financeHistory} closeFinanceReportCallback={() => setFinanceReportPopupOpen(false)} /> : null}
  {velocityReportPopupOpen ? <VelocityReportPopup velocityHistory={velocityHistory} team={localTeam} closeVelocityReportCallback={() => setVelocityReportPopupOpen(false)} /> : null}
  {teamMemberReportPopupOpen ? <TeamMemberReportPopup velocityHistory={velocityHistory} team={localTeam.filter(member => member.currentlyEmployed)} closeTeamMemberReportCallback={() => setTeamMemberReportPopupOpen(false)} /> : null}
  {releaseReportPopupOpen ? <ReleaseReportPopup releaseHistory={releaseHistory} closeReleaseReportCallback={() => setReleaseReportPopupOpen(false)} /> : null}
  {epicReportPopupOpen ? <EpicReportPopup issues={localIssues} sprint={sprint} mobileGame={mobileGame} closeEpicReportCallback={() => setEpicReportPopupOpen(false)} /> : null}
  {developerDetailsPopupOpen && selectedDeveloperId !== null ? 
    <DeveloperDetailsPopup 
      developer={localTeam.find(member => member.id === selectedDeveloperId)!} 
      velocityHistory={velocityHistory} 
      closeDeveloperDetailsCallback={closeDeveloperDetails}
      dismissEmployeeCallback={handleDismissEmployee}
      sprintInProgress={sprintProgress > 0 && sprintProgress < 11}
    /> : null}
  {hireEmployeePopupOpen ? 
    <HireEmployeePopup 
      availableDevelopers={getUnemployedDevelopers()} 
      hireEmployeeCallback={handleHireEmployee} 
    /> : null}
  {employeeQuitPopupOpen && quittingEmployeeId !== null ?
    <EmployeeQuitPopup
      employee={localTeam.find(member => member.id === quittingEmployeeId)!}
      closeAndOpenHireCallback={handleEmployeeQuit}
    /> : null}
  <main className="main-content">
    <section className="product-owner">
      <h2>Backlog 📥 ({filterBacklogIssues(localIssues.filter((issue) => issue.available <= sprint && issue.status === eIssueStatus.Open)).length})</h2>
      {unlockedFeatures.backlogFilter && (
        <BacklogFilter 
          filters={backlogFilters}
          onFilterChange={setBacklogFilters}
          availableEpics={getAvailableEpics()}
        />
      )}
      {/* Map through all localIssues that have an available day equal of greater than today and a status of Open and display them in a card */}
        {filterBacklogIssues(localIssues.filter((issue) => issue.available <= sprint && issue.status === eIssueStatus.Open)).map((issue, index) => (
          <UnplannedIssueCard key={index} issue={issue} onClickCallback={(id) => handleToggleIssueClick(id, eIssueStatus.Todo)} />
        ))}
    </section>
    <section className="swimlanes">
      <div className="swimlane todo">
        <h3>Todo 📋 ({localIssues.filter((issue) => issue.status === eIssueStatus.Todo).length})</h3>
        {localIssues.map((issue, index) => {
          if (issue.status === eIssueStatus.Todo) {
            return (
              <PlannedIssueCard key={index} issue={issue} onClickCallback={(id) => handleToggleIssueClick(id, eIssueStatus.Open)}/>
            );
          }
          return null;
        }
        )}
      </div>
      <div className="swimlane coding">
        <h3>Coding 💻 ({localIssues.filter((issue) => issue.status === eIssueStatus.InProgress).length})</h3>
        {localIssues.map((issue, index) => {
          if (issue.status === eIssueStatus.InProgress) {
            return (
              <PlannedIssueCard key={index} issue={issue} onClickCallback={(id) => handleToggleIssueClick(id, eIssueStatus.Open)}/>
            );
          }
          return null;
        }
        )}
      </div>
      <div className="swimlane qa">
        <h3>QA 🔍 ({localIssues.filter((issue) => issue.status === eIssueStatus.InQA).length})</h3>
        {localIssues.map((issue, index) => {
          if (issue.status === eIssueStatus.InQA) {
            return (
              <PlannedIssueCard key={index} issue={issue} onClickCallback={(id) => handleToggleIssueClick(id, eIssueStatus.Open)}/>
            );
          }
          return null;
        }
        )}
      </div>
      <div className="swimlane done">
        <h3>Done ✔️ ({localIssues.filter((issue) => issue.status === eIssueStatus.Done).length})</h3>
        {localIssues.map((issue, index) => {
          if (issue.status === eIssueStatus.Done) {
            return (
              <PlannedIssueCard key={index} issue={issue} onClickCallback={(id) => handleToggleIssueClick(id, eIssueStatus.Open)}/>
            );
          }
          return null;
        }
        )}
      </div>
    </section>
    <section className="scrum">
      <h2>Scrum 🫂</h2>
      <div className="scrum-card">
        <Sprint sprintNumber={sprint} sprintProgress={sprintProgress} startSprintCallback={() => { setSprint(sprint + 1); handleStartSprint(); }} />
      </div>
      <div className='release'>
        <h3>Release 🚀</h3>
        <ReleaseCard version={releaseVersion} storiesToAdd={localIssues.filter(i => i.status === eIssueStatus.Closed && !i.released).length} releaseCallback={handleRelease} sprintProgress={sprintProgress} />
      </div>
      {unlockedFeatures.velocity && (
        <>
          <h3>Velocity 📈</h3>
          <div className='scrum-card'>
            <SprintVelocity sprintVelocity={sprintVelocity} />
          </div>
        </>
      )}
      {unlockedFeatures.epics && (
        <>
          <h3>Epics 🏆</h3>
          <div>
            <EpicProgressCard issues={localIssues} sprint={sprint} mobileGame={mobileGame} />
          </div>
        </>
      )}
      <h3>Team Members ⚒️</h3>
      <div className="team-members-list">
            {localTeam.filter(member => member.currentlyEmployed).map((member) => (
                <TeamMemberCard key={member.id} member={member} onClickCallback={unlockedFeatures.teamMembers ? handleDeveloperClick : () => {}} />
            ))}
        </div>
    </section>
  </main>
  <footer className="footer">
    <Footer customerFeedback={customerFeedbackString} showFeedback={unlockedFeatures.customerFeedback} />
  </footer>
  </div>
  );
}

export default App;
